import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Coins, ArrowDown, ArrowUp, X, Bank, Lightning, CheckCircle } from "@phosphor-icons/react";

export default function Wallet() {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const [packages, setPackages] = useState([]);
    const [txns, setTxns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [polling, setPolling] = useState(false);
    const [showCashout, setShowCashout] = useState(false);
    const [cashing, setCashing] = useState(false);
    const [cashoutAmount, setCashoutAmount] = useState("");
    const [cashoutMethod, setCashoutMethod] = useState("paypal");
    const [cashoutDest, setCashoutDest] = useState("");
    const [cashoutNote, setCashoutNote] = useState("");
    const [autoPayout, setAutoPayout] = useState({ enabled: false, method: "paypal", destination: "", threshold: 10, interval_hours: 168, last_auto_payout_at: null });
    const [savingAuto, setSavingAuto] = useState(false);

    const loadAll = useCallback(async () => {
        const [p, t, ap] = await Promise.all([
            api.get("/wallet/packages"),
            api.get("/wallet/transactions"),
            api.get("/wallet/auto-payout"),
        ]);
        setPackages(p.data);
        setTxns(t.data);
        setAutoPayout(ap.data);
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const pollStatus = useCallback(async (sessionId, attempt = 0) => {
        if (attempt >= 8) {
            toast.error("Payment status check timed out — refresh in a moment.");
            setPolling(false);
            return;
        }
        try {
            const { data } = await api.get(`/wallet/checkout/status/${sessionId}`);
            if (data.payment_status === "paid" && data.credited) {
                toast.success(`+${data.coins} coins added!`);
                await refresh();
                await loadAll();
                nav("/wallet", { replace: true });
                setPolling(false);
                return;
            }
            if (data.status === "expired") {
                toast.error("Checkout expired. Try again.");
                setPolling(false);
                return;
            }
            setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
            setPolling(false);
        }
    }, [refresh, loadAll, nav]);

    useEffect(() => {
        const sid = params.get("session_id");
        if (sid && !polling) {
            setPolling(true);
            toast.info("Confirming payment…");
            pollStatus(sid);
        }
    }, [params, polling, pollStatus]);

    const buy = async (packageId) => {
        setLoading(true);
        try {
            const { data } = await api.post("/wallet/checkout", {
                package_id: packageId,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
            setLoading(false);
        }
    };

    const submitCashout = async (e) => {
        e.preventDefault();
        if (!cashoutDest.trim()) return toast.error("Provide a payout destination");
        const amt = parseFloat(cashoutAmount) || (user?.earnings || 0);
        if (amt <= 0) return toast.error("Invalid amount");
        setCashing(true);
        try {
            const { data } = await api.post("/wallet/payout", {
                amount: amt,
                method: cashoutMethod,
                destination: cashoutDest,
                note: cashoutNote,
            });
            toast.success(`Payout requested: $${data.amount.toFixed(2)} — admin will process within 3-5 days`);
            setShowCashout(false);
            setCashoutAmount(""); setCashoutDest(""); setCashoutNote("");
            await refresh();
            await loadAll();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setCashing(false); }
    };

    const saveAutoPayout = async (next) => {
        setSavingAuto(true);
        try {
            const { data } = await api.patch("/wallet/auto-payout", next);
            setAutoPayout((a) => ({ ...a, ...data, enabled: data.auto_payout_enabled, method: data.auto_payout_method, destination: data.auto_payout_destination, threshold: data.auto_payout_threshold }));
            toast.success(next.enabled ? "Auto-payout enabled" : "Auto-payout updated");
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setSavingAuto(false); }
    };

    return (
        <div className="px-4 md:px-10 py-8 max-w-3xl mx-auto">
            <div className="font-heading text-xs tracking-[0.4em] text-wta-muted">YOUR WALLET</div>
            <h1 className="font-heading text-5xl md:text-7xl tracking-tight uppercase">Coins & Payouts</h1>

            <div className="grid md:grid-cols-2 gap-4 mt-8">
                <div className="bg-wta-surface border border-wta-border p-6 rounded-sm">
                    <div className="text-xs tracking-widest uppercase text-wta-muted">Balance</div>
                    <div className="font-heading text-6xl mt-2 flex items-center gap-2 text-wta-accent">
                        <Coins size={40} weight="fill" /> {user?.coins ?? 0}
                    </div>
                    <div className="text-xs text-wta-muted mt-1">1 coin = $0.01</div>
                </div>
                <div className="bg-wta-surface border border-wta-border p-6 rounded-sm">
                    <div className="text-xs tracking-widest uppercase text-wta-muted">Earnings</div>
                    <div className="font-heading text-6xl mt-2 text-white">${(user?.earnings || 0).toFixed(2)}</div>
                    <button
                        data-testid="cashout-btn"
                        onClick={() => setShowCashout(true)}
                        disabled={(user?.earnings || 0) < 1}
                        className="btn-primary mt-3 px-5 py-2 rounded-sm text-sm disabled:opacity-50"
                    >
                        <Bank size={14} className="inline mr-1" /> Request Payout
                    </button>
                    <div className="text-xs text-wta-muted mt-2">Manual processing · 3-5 business days</div>
                </div>
            </div>

            {/* Coin packages */}
            <div className="bg-wta-surface border border-wta-border rounded-sm p-6 mt-4">
                <div className="font-heading text-2xl tracking-wider mb-4">Buy Coins</div>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {packages.map((p) => (
                        <button
                            key={p.id}
                            onClick={() => buy(p.id)}
                            disabled={loading || polling}
                            data-testid={`buy-pkg-${p.id}`}
                            className="text-left bg-wta-surface2 border border-wta-border rounded-sm p-4 hover:border-wta-primary transition disabled:opacity-50"
                        >
                            <div className="font-heading text-2xl tracking-wide">{p.label}</div>
                            <div className="font-heading text-4xl text-wta-accent mt-1 flex items-center gap-1">
                                <Coins size={20} weight="fill" /> {p.coins.toLocaleString()}
                            </div>
                            <div className="text-xs tracking-widest uppercase text-wta-muted mt-1">${p.usd.toFixed(2)} USD</div>
                        </button>
                    ))}
                </div>
                <div className="text-xs text-wta-muted mt-3">Secure checkout via Stripe — test card: <code className="text-wta-accent">4242 4242 4242 4242</code> · any future expiry · any CVC</div>
            </div>

            {/* Auto-payout */}
            <div className="bg-wta-surface border border-wta-border rounded-sm p-6 mt-4">
                <div className="flex items-center justify-between mb-1">
                    <div className="font-heading text-2xl tracking-wider flex items-center gap-2">
                        <Lightning size={20} weight="fill" className="text-wta-accent" /> Weekly Auto-Payout
                    </div>
                    <button
                        data-testid="auto-payout-toggle"
                        disabled={savingAuto}
                        onClick={() => saveAutoPayout({
                            enabled: !autoPayout.enabled,
                            method: autoPayout.method,
                            destination: autoPayout.destination,
                            threshold: autoPayout.threshold,
                        })}
                        className={`relative w-12 h-6 rounded-full transition ${autoPayout.enabled ? "bg-wta-primary" : "bg-wta-surface2 border border-wta-border"}`}
                    >
                        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${autoPayout.enabled ? "left-6" : "left-0.5"}`} />
                    </button>
                </div>
                <p className="text-xs text-wta-muted mb-4">
                    When enabled, your earnings auto-queue for payout once they reach your threshold (checked weekly).
                </p>

                <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                        <label className="text-xs tracking-widest text-wta-muted uppercase">Threshold (USD)</label>
                        <input
                            data-testid="auto-payout-threshold"
                            type="number" min="1" step="0.01"
                            value={autoPayout.threshold}
                            onChange={(e) => setAutoPayout((a) => ({ ...a, threshold: parseFloat(e.target.value) || 0 }))}
                            className="mt-1 w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-sm"
                        />
                    </div>
                    <div>
                        <label className="text-xs tracking-widest text-wta-muted uppercase">Method</label>
                        <select
                            data-testid="auto-payout-method"
                            value={autoPayout.method}
                            onChange={(e) => setAutoPayout((a) => ({ ...a, method: e.target.value }))}
                            className="mt-1 w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-sm"
                        >
                            <option value="paypal">PayPal</option>
                            <option value="bank">Bank</option>
                            <option value="crypto">Crypto (USDC)</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs tracking-widest text-wta-muted uppercase">Destination</label>
                        <input
                            data-testid="auto-payout-destination"
                            placeholder={autoPayout.method === "paypal" ? "email@example.com" : autoPayout.method === "bank" ? "Bank details" : "Wallet address"}
                            value={autoPayout.destination}
                            onChange={(e) => setAutoPayout((a) => ({ ...a, destination: e.target.value }))}
                            className="mt-1 w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-sm"
                        />
                    </div>
                </div>

                <div className="flex items-center justify-between mt-4">
                    <div className="text-xs text-wta-muted">
                        {autoPayout.last_auto_payout_at
                            ? <>Last auto-payout: <span className="text-white">{new Date(autoPayout.last_auto_payout_at).toLocaleString()}</span></>
                            : "No auto-payout sent yet."}
                    </div>
                    <button
                        data-testid="auto-payout-save"
                        disabled={savingAuto}
                        onClick={() => saveAutoPayout({
                            enabled: autoPayout.enabled,
                            method: autoPayout.method,
                            destination: autoPayout.destination,
                            threshold: autoPayout.threshold,
                        })}
                        className="btn-ghost px-4 py-2 rounded-sm text-sm font-heading uppercase tracking-wider disabled:opacity-50"
                    >
                        {savingAuto ? "Saving…" : "Save Settings"}
                    </button>
                </div>
                {autoPayout.enabled && autoPayout.destination && (
                    <div className="mt-3 text-xs text-wta-accent flex items-center gap-1">
                        <CheckCircle size={12} weight="fill" /> Active — earnings ≥ ${autoPayout.threshold} auto-queue weekly to {autoPayout.method}: {autoPayout.destination}
                    </div>
                )}
            </div>

            {/* Cashout modal */}
            {showCashout && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowCashout(false)}>
                    <form onSubmit={submitCashout} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-wta-surface border border-wta-border rounded-sm p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="font-heading text-2xl tracking-wider">Request Payout</div>
                            <button type="button" data-testid="close-cashout" onClick={() => setShowCashout(false)} className="text-wta-muted hover:text-white"><X size={20} /></button>
                        </div>
                        <p className="text-xs text-wta-muted mb-4">Available: ${(user?.earnings || 0).toFixed(2)}. Admin will manually process within 3-5 business days.</p>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs tracking-widest text-wta-muted uppercase">Amount (USD)</label>
                                <input
                                    data-testid="cashout-amount"
                                    type="number" min="1" step="0.01" max={user?.earnings || 0}
                                    value={cashoutAmount}
                                    onChange={(e) => setCashoutAmount(e.target.value)}
                                    placeholder={`${(user?.earnings || 0).toFixed(2)} (full)`}
                                    className="mt-1 w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-sm"
                                />
                            </div>
                            <div>
                                <label className="text-xs tracking-widest text-wta-muted uppercase">Method</label>
                                <select
                                    data-testid="cashout-method"
                                    value={cashoutMethod}
                                    onChange={(e) => setCashoutMethod(e.target.value)}
                                    className="mt-1 w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-sm"
                                >
                                    <option value="paypal">PayPal</option>
                                    <option value="bank">Bank Transfer</option>
                                    <option value="crypto">Crypto (USDC)</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-xs tracking-widest text-wta-muted uppercase">
                                    {cashoutMethod === "paypal" ? "PayPal Email" : cashoutMethod === "bank" ? "Bank Details" : "Wallet Address"}
                                </label>
                                <input
                                    data-testid="cashout-destination"
                                    required
                                    value={cashoutDest}
                                    onChange={(e) => setCashoutDest(e.target.value)}
                                    className="mt-1 w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-sm"
                                />
                            </div>
                            <div>
                                <label className="text-xs tracking-widest text-wta-muted uppercase">Note (optional)</label>
                                <textarea
                                    data-testid="cashout-note"
                                    rows={2}
                                    value={cashoutNote}
                                    onChange={(e) => setCashoutNote(e.target.value)}
                                    className="mt-1 w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-sm"
                                />
                            </div>
                        </div>
                        <button data-testid="cashout-submit" disabled={cashing} className="mt-5 w-full btn-primary py-3 rounded-sm text-base disabled:opacity-50">
                            {cashing ? "Submitting…" : "Submit Payout Request"}
                        </button>
                    </form>
                </div>
            )}

            {/* Transactions */}
            <div className="mt-10">
                <div className="font-heading text-3xl tracking-wider uppercase mb-4">Transactions</div>
                <div className="bg-wta-surface border border-wta-border rounded-sm overflow-hidden">
                    {txns.length === 0 ? (
                        <div className="p-6 text-center text-wta-muted text-sm">No transactions yet</div>
                    ) : txns.map((t, i) => {
                        const negative = ["entry_fee", "gift_sent", "trivia_entry", "paid_vote"].includes(t.type);
                        const isPayout = t.type === "payout";
                        const statusColor = t.status === "completed" ? "text-wta-accent" : t.status === "rejected" ? "text-wta-primary" : "text-wta-muted";
                        return (
                            <div key={t.id} className={`flex items-center gap-4 px-5 py-3 ${i !== txns.length - 1 ? "border-b border-wta-border" : ""}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isPayout ? "bg-wta-secondary/15 text-wta-secondary" : negative ? "bg-wta-primary/15 text-wta-primary" : "bg-wta-accent/15 text-wta-accent"}`}>
                                    {isPayout ? <Bank size={14} weight="bold" /> : negative ? <ArrowUp size={14} weight="bold" /> : <ArrowDown size={14} weight="bold" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm capitalize">{t.type.replace(/_/g, " ")}</div>
                                    <div className={`text-xs ${statusColor}`}>{new Date(t.created_at).toLocaleString()} · {t.status}</div>
                                </div>
                                <div className={`font-heading text-xl ${negative || isPayout ? "text-wta-primary" : "text-wta-accent"}`}>
                                    {(negative || isPayout) ? "-" : "+"}${t.amount.toFixed(2)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
